import { useMutation, useQuery, useQueryClient } from 'react-query'
import type {
  Comment,
  CommentStatus,
  Notification,
  NotificationSubscription,
  SubscriptionStatus,
} from '~/types/activity.types'
import httpClient from '~/utils/httpClient'

// --- Notifications ---

export const useNotifications = (params?: {
  page?: number
  pageSize?: number
  unreadOnly?: boolean
  getAll?: boolean
}) => {
  return useQuery({
    queryKey: ['notifications', params],
    queryFn: async () => {
      const response = await httpClient.get<{ data: Notification[] }>(
        '/api/activities/notifications',
        {
          params,
        },
      )
      return response?.data.data
    },
  })
}

export const useNotificationHistory = (params?: { page?: number; pageSize?: number }) => {
  return useQuery({
    queryKey: ['notifications-history', params],
    queryFn: async () => {
      const response = await httpClient.get<{ data: Notification[] }>(
        '/api/activities/notifications/history',
        {
          params,
        },
      )
      return response?.data.data
    },
  })
}

export const useMarkReadMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await httpClient.patch<{ data: Notification }>(
        `/api/activities/notifications/${id}/read`,
      )
      return response?.data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications'])
      queryClient.invalidateQueries(['notifications-history'])
    },
  })
}

export const useMarkAllReadMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await httpClient.post('/api/activities/notifications/read-all')
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications'])
    },
  })
}

// --- Comments ---

export const useComments = (params: { docId?: number; controlId?: number }) => {
  return useQuery({
    queryKey: ['comments', params],
    queryFn: async () => {
      const response = await httpClient.get<{ data: Comment[] }>('/api/activities/comments', {
        params,
      })
      return response?.data.data
    },
    enabled: !!(params.docId || params.controlId),
  })
}

export const useCreateCommentMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      docId?: number | null
      controlId?: number | null
      replyCommentId?: number | null
      text: string
      status?: number
      mentionedUserIds?: number[]
    }) => {
      const response = await httpClient.post<{ data: Comment }>('/api/activities/comments', payload)
      return response?.data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['comments'])
      queryClient.invalidateQueries(['notifications']) // Creating a comment triggers notifications for others
    },
  })
}

export const useUpdateCommentMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: CommentStatus }) => {
      const response = await httpClient.patch<{ data: Comment }>(`/api/activities/comments/${id}`, {
        status,
      })
      return response?.data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['comments'])
    },
  })
}

// --- Subscriptions ---

export const useSubscription = (params: { docId?: number; controlId?: number }) => {
  return useQuery({
    queryKey: ['subscriptions', params],
    queryFn: async () => {
      const response = await httpClient.get<{ data: NotificationSubscription | null }>(
        '/api/activities/subscriptions',
        { params },
      )
      return response?.data.data
    },
    enabled: !!(params.docId || params.controlId),
  })
}

export const useUpdateSubscriptionMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      docId?: number | null
      controlId?: number | null
      subscriptionStatus: SubscriptionStatus
    }) => {
      const response = await httpClient.post<{ data: NotificationSubscription }>(
        '/api/activities/subscriptions',
        payload,
      )
      return response?.data.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([
        'subscriptions',
        { docId: variables.docId, controlId: variables.controlId },
      ])
    },
  })
}
